import { Location } from '@angular/common';
import { DOCUMENT, inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AppService, CURRENT_CLIENT_ID_KEY } from '@core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { ClientManagementService } from '@services';
import { NotificationsService } from '@ui';
import { map, of, tap } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';

import { ClientManagementActions } from './clients-managements.actions';

@Injectable()
export class ClientsManagementsEffects {
  private actions$ = inject(Actions);
  private router = inject(Router);
  private clientManagementService = inject(ClientManagementService);
  private appService = inject(AppService);
  private notificationService = inject(NotificationsService);
  private document = inject(DOCUMENT);
  private location = inject(Location);

  /**
   * Effect that changes the current client ID in the URL
   * It uses the Location service to update the URL without triggering route events
   * TODO: revise flow how we manage client id in the url
   */
  clientIdChanged$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ClientManagementActions.clientIdChanged),
      tap(({ clientId }) => {
        const url = new URL(this.document.location.href);

        url.searchParams.set(CURRENT_CLIENT_ID_KEY, clientId);
        this.location.replaceState(url.pathname + url.search);
      }),
    );
  }, { dispatch: false });

  loadClientList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ClientManagementActions.loadChildClients),
      switchMap(() =>
        this.clientManagementService.getClientAccessList().pipe(
          map(clientList => ClientManagementActions.loadChildClientsSuccess({ clientList })),
          catchError(error => of(ClientManagementActions.clientManagementError({ error }))),
        ),
      ),
    ),
  );

  addNewClient$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ClientManagementActions.addChildClient),
      switchMap(action =>
        this.clientManagementService.createChildClient(this.appService.managingClientId, action.newUserData).pipe(
          map(client => ClientManagementActions.addChildClientSuccess({ newClient: client })),
          catchError(error => of(ClientManagementActions.clientManagementError({ error }))),
        ),
      ),
    ),
  );

  updateClient$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ClientManagementActions.updateChildClient),
      switchMap(action =>
        this.clientManagementService
          .updateChildClient(this.appService.managingClientId, action.clientClientId, action.clientData)
          .pipe(
            map(client => ClientManagementActions.updateChildClientSuccess({ childClient: client })),
            catchError(error => of(ClientManagementActions.clientManagementError({ error }))),
          ),
      ),
    ),
  );

  updateClientRequireTotp$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ClientManagementActions.updateClientRequireTotp),
      switchMap(action =>
        this.clientManagementService
          .updateClientRequireTotp(action.clientClientId, action.requireTotp)
          .pipe(
            map(client => ClientManagementActions.updateClientRequireTotpSuccess({ childClient: client })),
            catchError(error => of(ClientManagementActions.clientManagementError({ error }))),
          ),
      ),
    ),
  );

  updateClientSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ClientManagementActions.updateChildClientSuccess),
        tap(action =>
          this.notificationService.showSuccessMessage(
            `Client ${action.childClient.friendlyName} is successfully updated`,
          ),
        ),
      ),
    { dispatch: false },
  );

  updateClientRequireTotpSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ClientManagementActions.updateClientRequireTotpSuccess),
        tap(action =>
          this.notificationService.showSuccessMessage(
            `Client ${action.childClient.friendlyName} is successfully updated`,
          ),
        ),
      ),
    { dispatch: false },
  );

  clientManagementError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(ClientManagementActions.clientManagementError),
        tap(async error => {
          this.notificationService.showErrorMessage('Error:' + error.error.status);
          await this.router.navigate(['client-management']);
        }),
      ),
    { dispatch: false },
  );
}
